import { useUser } from 'core/lite/hooks/useUser';
import { Button, TextHtml } from 'ui';
import * as coreEntities from 'core/store/index';
import { CLIENT } from 'core/variables/constant';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import InputText from 'ui/atoms/InputText/InputText';
import FormField from 'ui/molecules/FormField/FormField';
import { getSignUpLicenseErrorKey } from './signUpLicenseErrors';

// Sin respuesta del backend o sin mensaje que mapear: se pinta el error genérico, no el de código inválido.
export const REDEEM_REQUEST_FAILED = 'Redeem request failed';

// Algunos consumidores aún pasan `error`, pero se ignora: el estado de error sale de `errorMsg`.
const AddLicenseForm = ({ onSubmit = () => null, updateValue = () => null, errorMsg = null, isLoading, label = null, shouldHandleSubmit = true, t }) => {
  const { errors, register, handleSubmit, watch, reset } = useForm();
  const { isStudent } = useUser();
  const client = useSelector((state) => coreEntities.organization.selectors.getClient(state));
  const [customError] = useState(t('course:This license code is from ByME Digital')); // lo pongo así por si algun día hay mas, que sea dinamico
  const formRef = useRef(null);
  const wasLoadingRef = useRef(isLoading);

  // «Activar» se deshabilita al canjear y el foco cae al <body>; si falla, va al campo que describe el error.
  useEffect(() => {
    const hasFailed = wasLoadingRef.current && !isLoading && !!errorMsg;
    wasLoadingRef.current = isLoading;
    if (!hasFailed) return;
    const { activeElement } = document;
    const isFocusLost = !activeElement || activeElement === document.body || formRef.current?.contains(activeElement);
    if (isFocusLost) formRef.current?.elements.namedItem('code')?.focus();
  }, [isLoading, errorMsg]);

  function showError() {
    if (errors.code?.type === 'required') return t('common:Required parameter');
    const signUpErrorKey = getSignUpLicenseErrorKey(errorMsg);
    if (signUpErrorKey) return t(signUpErrorKey);
    if (errorMsg === REDEEM_REQUEST_FAILED) return t('common:An error has occurred');
    if (errorMsg === 'Code already linked' || errorMsg === 'No left uses') return t('new-course:Code already linked');
    if (errorMsg === 'Code expired') return t('new-course:Code expired');
    if (errorMsg === 'License role constraint') return t('new-course:License role constraint');
    if (errorMsg === 'License country mismatch') return t('new-course:License country mismatch');
    else return isStudent ? t('new-course:Invalid license student') : t('new-course:Invalid license');
  }

  function getIsThereCustomError(value) {
    let isThereCustomError = false;
    if (value && client === CLIENT.EDELVIVES) {
      // Convertir la cadena a minúsculas para realizar una comparación sin distinción de mayúsculas y minúsculas
      const valueLower = value.toLowerCase();
      // Verificar si la cadena comienza con "st-" o "tr-"
      if (valueLower.startsWith('st-') || valueLower.startsWith('tr-')) {
        isThereCustomError = true;
      }
    }
    return isThereCustomError;
  }

  function handleFormSubmit(data) {
    if (isLoading) return;
    onSubmit(data, reset); // Pasa reset como un callback para borrar el input después de una sumisión exitosa
  }

  const code = watch('code');
  const hasCustomError = getIsThereCustomError(code);

  return (
    <div>
      {/* Sin noValidate, el `required` nativo que pone FormField aborta el submit y react-hook-form no pinta el error. */}
      <form ref={formRef} noValidate onSubmit={handleSubmit(handleFormSubmit)} className="add-license-form">
        <div className="add-license-form__input">
          {/* Etiqueta visible y sin placeholder que la repita: el placeholder desaparece al escribir y no
              sirve de etiqueta (WCAG 3.3.2, TAN-10041). Si el consumidor pasa `label` (la instrucción de
              «Añadir un código», TAN-11129), es esa; si no, la genérica. Con el código de ByME el campo se
              marca inválido, pero el mensaje genérico solo sale con un error del backend (`errorMsg`). */}
          <FormField
            error={!!errors.code || !!errorMsg || hasCustomError}
            errorMessage={errors.code || errorMsg ? showError() : undefined}
            isRequired
            label={label || t('Enter a valid code')}
          >
            <InputText onChange={({ target: { value } }) => updateValue(value)} name="code" referer={register({ required: true })} />
          </FormField>
        </div>
        {shouldHandleSubmit && (
          <div className="add-license-form__button">
            <Button text={t('actions:Activate')} type="submit" isDisabled={!code || hasCustomError} isFullWidth isLoading={isLoading} />
          </div>
        )}
      </form>
      {/* Región viva siempre montada: así el aviso de ByME se anuncia al aparecer (WCAG 4.1.3) y explica
          por qué «Activar» queda deshabilitado. */}
      <div role="alert">
        {hasCustomError && (
          <div className="add-license-form__custom-error-container">
            <TextHtml text={customError} className="add-license-form__custom-error" />
          </div>
        )}
      </div>
    </div>
  );
};

AddLicenseForm.propTypes = {
  onSubmit: PropTypes.func,
  updateValue: PropTypes.func,
  errorMsg: PropTypes.string,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  shouldHandleSubmit: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation(['new-course', 'actions', 'placeholder'])(AddLicenseForm);
